import React, { useState } from 'react';
import './CategorySelection.css';
import categories from '../../types/categories';

type CategorySelectionProps = {
    onCategorySelect: (category: keyof typeof categories | null, subcategory?: string) => void;
    onSearchChange: (searchTerm: string) => void;
};



const RUNE_COLOR_CSS: Record<string, string> = {
    green: '#4CAF50', orange: '#FF9800', blue: '#2196F3', red: '#f44336',
    aqua: '#00BCD4', yellow: '#FFEB3B', gray: '#9E9E9E', purple: '#9C27B0',
};

const CategorySelection: React.FC<CategorySelectionProps> = ({ onCategorySelect, onSearchChange }) => {
    const [selectedCategory, setSelectedCategory] = useState<keyof typeof categories | null>(null);
    const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(null);

    const handleCategoryClick = (category: keyof typeof categories) => {
        const newCategory = selectedCategory === category ? null : category;
        setSelectedCategory(newCategory);
        setSelectedSubcategory(null);
        onCategorySelect(newCategory);
    };

    const handleSubcategoryClick = (category: keyof typeof categories, subcategory: string) => {
        setSelectedCategory(category);
        setSelectedSubcategory(subcategory);
        onCategorySelect(category, subcategory);
    };

    const renderSubcategories = (category: keyof typeof categories) => {
        let subcategoryList: string[] = [];
        
        if (category === "Weapon") subcategoryList = ["Axe", "Sword", "Mace", "Dagger", "Staff", "Chain"];
        else if (category === "Armor") subcategoryList = ["Head", "Shoulder", "Torso", "Hand", "Leg", "Feet"];
        else if (category === "Accessory") subcategoryList = ["Amulet", "Armring", "Cloak", "Neck", "Ring"];
        else if (category === "Knowledge") subcategoryList = ["Blueprint", "Book", "Scroll"];
        else if (category === "Rune") subcategoryList = ["green", "orange", "blue", "red", "aqua", "yellow", "gray", "purple"];
        else subcategoryList = categories[category] as unknown as string[];

        if (subcategoryList.length === 0) return null;

        return (
            <div className="cs-subcategories">
                {subcategoryList.map((sub) => {
                    const isRuneColor = category === "Rune";
                    return (
                        <button
                            key={sub}
                            className={`cs-subcategory ${selectedSubcategory === sub ? 'active' : ''} ${isRuneColor ? 'cs-rune' : ''}`}
                            onClick={() => handleSubcategoryClick(category, sub)}
                            style={isRuneColor ? { '--rune-color': RUNE_COLOR_CSS[sub] || '#aaa' } as React.CSSProperties : undefined}
                        >
                            {isRuneColor && (
                                <span className="cs-rune-dot" style={{ background: RUNE_COLOR_CSS[sub] || '#aaa' }} />
                            )}
                            <span>{sub.charAt(0).toUpperCase() + sub.slice(1)}</span>
                        </button>
                    );
                })}
            </div>
        );
    };    

    return (
        <div className="cs-root">
            <div className="cs-search-wrap">
                <input
                    type="text"
                    placeholder="Search items..."
                    onChange={(e) => onSearchChange(e.target.value)}
                    className="cs-search-input"
                />
            </div>

            <div className="cs-section-label">Categories</div>

            <div className="cs-categories">
                {Object.keys(categories).map((category) => (
                    <div key={category} className="cs-group">
                        <button
                            className={`cs-category ${selectedCategory === category ? 'active' : ''}`}
                            onClick={() => handleCategoryClick(category as keyof typeof categories)}
                        >
                            <span className="cs-cat-name">{category}</span>
                            {(categories[category as keyof typeof categories] as readonly string[]).length > 0 && (
                                <span className={`cs-cat-arrow ${selectedCategory === category ? 'open' : ''}`}>›</span>
                            )}
                        </button>
                        {selectedCategory === category && renderSubcategories(category as keyof typeof categories)}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default CategorySelection;
